import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Car, DollarSign, Gift, Wallet, X, House } from 'lucide-react';
import { Button } from '@/components/ui/button';
import RideDriverTrips from './RideDriverTrips';
import RideDriverEarnings from './RideDriverEarnings';
import RideDriverWallet from './RideDriverWallet';

type MenuView = 'menu' | 'trips' | 'earnings' | 'rewards' | 'wallet';

const RideDriverMenu = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>('menu');

  type MenuItem = {
    key: 'home' | MenuView;
    label: string;
    icon: any;
    description: string;
    action?: () => void;
  };

  const menuItems: MenuItem[] = [
    { key: 'home', label: 'Home', icon: House, description: 'Back to home page', action: () => { setOpen(false); navigate('/home'); } },
    { key: 'trips', label: 'Trips', icon: Car, description: 'View completed rides' },
    { key: 'earnings', label: 'Earnings', icon: DollarSign, description: 'Your earnings breakdown' },
    { key: 'rewards', label: 'Rewards', icon: Gift, description: 'Bonuses & incentives' },
    { key: 'wallet', label: 'Wallet', icon: Wallet, description: 'Manage your balance' },
  ];

  const handleBack = () => setView('menu');

  return (
    <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setView('menu'); }}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="text-foreground">
          <Menu className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 overflow-y-auto">
        {view === 'menu' ? (
          <div className="p-6">
            <SheetHeader className="mb-8">
              <SheetTitle className="text-2xl font-bold">Menu</SheetTitle>
            </SheetHeader>
            <div className="space-y-2">
              {menuItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => item.action ? item.action() : setView(item.key as MenuView)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-accent transition-colors text-left"
                >
                  <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : view === 'trips' ? (
          <RideDriverTrips onBack={handleBack} />
        ) : view === 'earnings' ? (
          <RideDriverEarnings onBack={handleBack} />
        ) : view === 'rewards' ? (
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Button variant="ghost" size="icon" onClick={handleBack}><X className="w-5 h-5" /></Button>
              <h2 className="text-2xl font-bold">Rewards</h2>
            </div>
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Gift className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No rewards yet</h3>
              <p className="text-muted-foreground text-sm">Complete more rides to earn rewards and bonuses.</p>
            </div>
          </div>
        ) : (
          <RideDriverWallet onBack={handleBack} />
        )}
      </SheetContent>
    </Sheet>
  );
};

export default RideDriverMenu;
